import Vue from 'vue'
import {Dictionary, Module} from 'vuex'
import {RootState, WikiState} from '@/types/store/states'
import {RawWikiNode, RawWikiPage} from '@/types/entity/RawWiki'
import WikiPage from '@/models/wiki/wikiPage'
import WikiNode from '@/models/wiki/wikiNode'
import $store from '@/store'
import {API} from '@/constants'


const store: Module<WikiState, RootState> = {
  namespaced: true,
  state: {
    pages: {},
    nodes: {}
  },
  getters: {
    getWikiPage: state => (data: RawWikiPage) => {
      if (state.pages[data.id]) {
        state.pages[data.id].setData(data)
      } else {
        Vue.set(state.pages, data.id, new WikiPage(data))
      }
      return state.pages[data.id]
    },
    getWikiNode: state => (data: RawWikiNode) => {
      if (state.nodes[data.id]) {
        state.nodes[data.id].setData(data)
      } else {
        Vue.set(state.nodes, data.id, new WikiNode(data))
      }
      return state.nodes[data.id]
    },
    getWikiNodes: (state, getters) => (nodesData: RawWikiNode[], returnArray: boolean = true) => {
      const result = returnArray ? [] : {};
      for (const nodeData of nodesData) {
        const node = getters.getWikiNode(nodeData);
        if (returnArray) {
          (result as WikiNode[]).push(node);
        } else {
          (result as { [nodeId: string]: WikiNode })[node.getId()] = node;
        }
      }
      return result;
    },
    getWikiPages: (state, getters) => (pagesData: RawWikiPage[], returnArray: boolean = true) => {
      const result = returnArray ? [] : {};
      for (const pageData of pagesData) {
        const page = getters.getWikiPage(pageData);
        if (returnArray) {
          (result as WikiPage[]).push(page);
        } else {
          (result as { [pageId: string]: WikiPage })[page.getId()] = page;
        }
      }
      return result;
    },
  },
  mutations: {
  },
  actions: {
    async createPage (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageCreate,
        data
      }, {root: true})
      return response;
    },
    async getPage (context, id) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageGet,
        data: {id},
      }, {root: true})
      return context.getters.getWikiPage(response);
    },
    async updatePage (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageUpdate,
        data
      }, {root: true})
      return response;
    },
    async pageParent (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageParent,
        data
      }, {root: true})
      return response;
    },
    async deletePage (context, id) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageDelete,
        data: {id},
      }, {root: true})
      return response;
    },
    async nodeList (context, data = {}) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeList,
        data
      }, {root: true})
      return context.getters.getWikiNodes(response.nodes);
    },
    async pageList (context, parent) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.pageList,
        data: {parent}
      }, {root: true})
      return context.getters.getWikiPages(response.pages);
    },
    async getNode (context, id) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeGet,
        data: {id},
      }, {root: true})
      return context.getters.getWikiNode(response);
    },
    async createNode (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeCreate,
        data
      }, {root: true})
      return response;
    },
    async updateNode (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeUpdate,
        data
      }, {root: true})
      return response;
    },
    async nodeParent (context, data) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeParent,
        data
      }, {root: true})
      return response;
    },
    async deleteNode (context, id) {
      const response = await context.dispatch('apiRequest', {
        method: API.wiki.nodeDelete,
        data: {id},
      }, {root: true})
      return response;
    },
    async getNodesTree (context, id) {
      let nodes = await context.dispatch('nodeList');
      let map: Dictionary<WikiNode> = {};
      for (let node of nodes) {
        map[node.getId()] = node;
      }

      for (let node of nodes) {
        let parent = map[node.getParentId()]
        if (parent) {
          parent.addChildren(node);
        }
      }
      let tree: WikiNode[] = [];
      for (let node of nodes) {
        if (!map[node.getParentId()]) {
          tree.push(node);
        }
      }
      return tree;
    }
  }
}

export default store